import React from 'react';
import styled from 'styled-components';
import { Tooltip } from 'antd';
import Icon from 'components/icons';
import { SortDirection } from 'types/sorting';

interface SortableHeaderProps {
  label: string;
  columnKey: string;
  direction: SortDirection;
  onToggle: (columnKey: string) => void;
  disabled?: boolean;
  tooltip?: string;
  extra?: React.ReactNode;
}

function getChevronIcon(
  direction: SortDirection
): 'ChevronUpPurple' | 'ChevronDownPurple' | 'ChevronDown' {
  if (direction === 'asc') return 'ChevronUpPurple';
  if (direction === 'desc') return 'ChevronDownPurple';
  return 'ChevronDown';
}

function getAriaSort(direction: SortDirection): 'none' | 'ascending' | 'descending' {
  if (direction === 'asc') return 'ascending';
  if (direction === 'desc') return 'descending';
  return 'none';
}

const LabelGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex-wrap: nowrap;
`;

const LabelText = styled.span`
  white-space: nowrap;
`;

const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  flex-shrink: 0;
`;

const ExtraSlot = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

const ChevronBox = styled.span`
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 4px;
  transition: background-color 0.2s ease, color 0.2s ease;
`;

const chevronStyle = { width: '8px', height: '5px' };

const HeaderButton = styled.div<{ $active: boolean; $disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 5px;
  width: 100%;
  box-sizing: border-box;
  min-height: 45px;
  position: relative;
  isolation: isolate;
  margin: 0;
  padding: 10px 8px 10px 0;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  user-select: none;
  text-align: left;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.default.colors.palette.Business_LightThemeGreyDarker};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  background: transparent;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 4px;
    background: transparent;
    transition: background-color 0.2s ease;
    z-index: -1;
  }

  ${ChevronBox} {
    color: ${({ theme }) => theme.default.colors.palette.Business_LightThemeGrayDark};
  }

  ${InfoIcon} svg {
    width: 14px;
    height: 14px;
  }

  ${({ $active, theme }) =>
    $active &&
    `
      ${ChevronBox} {
        background-color: ${theme.default.colors.palette.Business_LightThemeGreyLighter};
        color: var(--lilac);
      }
    `}

  &:hover {
    &::before {
      background: ${({ $disabled, theme }) =>
        $disabled ? 'transparent' : theme.default.colors.palette.Business_LightThemeGreyLighter};
    }
  }

  &:hover ${ChevronBox} {
    color: ${({ theme }) => theme.default.colors.palette.Business_LightThemeGreyDarker};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.default.colors.dataVisualization.purple.base};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const SortableHeader: React.FC<SortableHeaderProps> = ({
  label,
  columnKey,
  direction,
  onToggle,
  disabled = false,
  tooltip,
  extra,
}) => {
  const isActive = direction !== null;

  const handleClick = () => {
    if (!disabled) onToggle(columnKey);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onToggle(columnKey);
    }
  };

  return (
    <HeaderButton
      role="columnheader"
      aria-sort={getAriaSort(direction)}
      aria-label={`${label}, ${getAriaSort(direction)}`}
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      $active={isActive}
      $disabled={disabled}
    >
      <LabelGroup>
        <LabelText>{label}</LabelText>
        {tooltip && (
          <Tooltip title={tooltip} placement="right" overlayInnerStyle={{ textAlign: 'center' }}>
            <InfoIcon onClick={(e) => e.stopPropagation()}>
              <Icon data-testid="info-inactive-icon" icon="Info" fill="var(--lilac)" size="small" />
            </InfoIcon>
          </Tooltip>
        )}
      </LabelGroup>
      {extra && (
        <ExtraSlot onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          {extra}
        </ExtraSlot>
      )}
      <ChevronBox>
        <Icon icon={getChevronIcon(direction)} style={chevronStyle} />
      </ChevronBox>
    </HeaderButton>
  );
};

export default SortableHeader;
